import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
  FaDatabase, 
  FaDownload, 
  FaUpload, 
  FaTrash, 
  FaRedo, 
  FaExclamationTriangle,
  FaCheck,
  FaInfoCircle,
  FaTable,
  FaFilter,
  FaFileExport,
  FaSearch,
  FaUsers,
  FaArrowLeft
} from 'react-icons/fa';
import './Admin.css';

const DatabaseManagement = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dbStatus, setDbStatus] = useState({
    status: 'unknown',
    connectionTime: null,
    version: null,
    size: null
  });
  const [downloadHistory, setDownloadHistory] = useState({});
  const [backupStatus, setBackupStatus] = useState({
    inProgress: false,
    lastBackup: null,
    error: null
  });
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionData, setCollectionData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [backupFile, setBackupFile] = useState(null);
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  
  // Client filtering state
  const [clients, setClients] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [filterApplied, setFilterApplied] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [modalCollection, setModalCollection] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [modalAllData, setModalAllData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalPosition, setModalPosition] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Modal filter state
  const [modalSelectedUser, setModalSelectedUser] = useState('');
  const [modalSelectedOrg, setModalSelectedOrg] = useState('');
  const [modalFilterApplied, setModalFilterApplied] = useState(false);

  useEffect(() => {
    fetchDatabaseStatus();
    fetchCollections();
    fetchLastBackupInfo();
    fetchClients();
  }, []);

  const fetchDatabaseStatus = async () => {
    try {
      const res = await axios.get('/api/admin/database/status');
      setDbStatus(res.data);
    } catch (err) {
      console.error('Error fetching database status:', err);
      setDbStatus({
        status: 'error',
        error: err.message || 'Failed to connect to database'
      });
    }
  };

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/admin/database/collections');
      setCollections(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching collections:', err);
      setError('Failed to load database collections. Please try again.');
      setLoading(false);
    }
  };

  const fetchLastBackupInfo = async () => {
    try {
      const res = await axios.get('/api/admin/database/backup/last');
      setBackupStatus({
        ...backupStatus,
        lastBackup: res.data.timestamp,
        error: null
      });
    } catch (err) {
      console.error('Error fetching last backup info:', err);
      setBackupStatus({
        ...backupStatus,
        error: 'Failed to get backup information'
      });
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get('/api/admin/database/clients');
      setClients(res.data.users);
      setOrganizations(res.data.organizations);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to load client information. Please try again.');
    }
  };

  const handleCollectionClick = async (collection) => {
    try {
      setSelectedCollection(collection);
      setDataLoading(true);
      setFilterApplied(false);
      
      // Reset filters when selecting a new collection
      setSelectedClient('');
      setSelectedOrganization('');
      
      const res = await axios.get(`/api/admin/database/collections/${collection.name}/sample`);
      setCollectionData(res.data);
      setDataLoading(false);
    } catch (err) {
      console.error(`Error fetching collection ${collection.name} data:`, err);
      setError(`Failed to load data from ${collection.name}. ${err.message}`);
      setDataLoading(false);
    }
  };
  
  const handleApplyFilter = async () => {
    if (!selectedCollection) return;
    
    try {
      setDataLoading(true);
      setFilterApplied(true);
      
      const params = {};
      if (selectedClient) params.clientId = selectedClient;
      if (selectedOrganization) params.organizationName = selectedOrganization;
      
      const res = await axios.get(`/api/admin/database/collections/${selectedCollection.name}/data`, { params });
      setCollectionData(res.data);
      setDataLoading(false);
    } catch (err) {
      console.error(`Error fetching filtered data:`, err);
      setError(`Failed to apply filters. ${err.message}`);
      setDataLoading(false);
      setFilterApplied(false);
    }
  };
  
  const handleClearFilters = async () => {
    setSelectedClient('');
    setSelectedOrganization('');
    setFilterApplied(false);
    
    if (selectedCollection) {
      handleCollectionClick(selectedCollection);
    }
  };
  
  // Modal functions
  const handleViewCollection = async (collection, e) => {
    e.stopPropagation();
    setModalVisible(true);
    setModalCollection(collection);
    setModalLoading(true);
    setModalPosition({ x: 50, y: 50 }); // Reset position
    
    // Reset modal filters
    setModalSelectedUser('');
    setModalSelectedOrg('');
    setModalFilterApplied(false);
    
    try {
      // Get all data without filters for the modal
      const res = await axios.get(`/api/admin/database/collections/${collection.name}/data`, {});
      setModalData(res.data);
      setModalAllData(res.data); // Store all data for filtering
      setModalLoading(false);
    } catch (err) {
      console.error(`Error fetching collection data for modal:`, err);
      setError(`Failed to load data for modal. ${err.message}`);
      setModalLoading(false);
    }
  };
  
  const handleCloseModal = () => {
    setModalVisible(false);
    setModalCollection(null);
    setModalData([]);
    setModalAllData([]);
    setModalSelectedUser('');
    setModalSelectedOrg('');
    setModalFilterApplied(false);
  };
  
  const handleApplyModalFilter = () => {
    if (!modalAllData.length) return;
    
    let filteredData = [...modalAllData];
    
    // Apply user filter if selected
    if (modalSelectedUser) {
      filteredData = filteredData.filter(item => 
        item.userId === modalSelectedUser || 
        item.createdBy === modalSelectedUser ||
        item.user === modalSelectedUser
      );
    }
    
    // Apply organization filter if selected
    if (modalSelectedOrg) {
      filteredData = filteredData.filter(item => 
        item.organizationName === modalSelectedOrg || 
        item.organization === modalSelectedOrg
      );
    }
    
    setModalData(filteredData);
    setModalFilterApplied(true);
  };
  
  const handleClearModalFilter = () => {
    setModalSelectedUser('');
    setModalSelectedOrg('');
    setModalData(modalAllData);
    setModalFilterApplied(false);
  };
  
  const handleModalExport = () => {
    if (!modalCollection) return;
    
    // Create query parameters based on modal filters
    const params = {};
    if (modalFilterApplied) {
      if (modalSelectedUser) params.clientId = modalSelectedUser;
      if (modalSelectedOrg) params.organizationName = modalSelectedOrg;
    }
    
    handleExportCollection(modalCollection.name, params);
  };
  
  const handleModalMouseDown = (e) => {
    if (e.target.className.includes('modal-header')) {
      setIsDragging(true);
      const rect = e.currentTarget.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };
  
  const handleModalMouseMove = (e) => {
    if (isDragging) {
      const x = e.clientX - dragOffset.x;
      const y = e.clientY - dragOffset.y;
      setModalPosition({ x, y });
    }
  };
  
  const handleModalMouseUp = () => {
    setIsDragging(false);
  };
  
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleModalMouseMove);
      document.addEventListener('mouseup', handleModalMouseUp);
    } else {
      document.removeEventListener('mousemove', handleModalMouseMove);
      document.removeEventListener('mouseup', handleModalMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleModalMouseMove);
      document.removeEventListener('mouseup', handleModalMouseUp);
    };
  }, [isDragging]);

  const handleBackupDatabase = async () => {
    try {
      setBackupStatus({
        ...backupStatus,
        inProgress: true,
        error: null
      });
      
      const res = await axios.post('/api/admin/database/backup');
      
      // Create a download link for the backup file
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `database-backup-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setBackupStatus({
        inProgress: false,
        lastBackup: new Date().toISOString(),
        error: null
      });
    } catch (err) {
      console.error('Error backing up database:', err);
      setBackupStatus({
        inProgress: false,
        error: err.message || 'Failed to backup database'
      });
    }
  };

  const handleFileChange = (e) => {
    setBackupFile(e.target.files[0]);
  };

  const handleRestoreDatabase = async () => {
    if (!backupFile) {
      alert('Please select a backup file to restore');
      return;
    }
    
    if (!window.confirm('WARNING: Restoring a database will overwrite all current data. This action cannot be undone. Are you sure you want to proceed?')) {
      return;
    }
    
    try {
      setRestoreInProgress(true);
      
      const formData = new FormData();
      formData.append('backup', backupFile);
      
      await axios.post('/api/admin/database/restore', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setRestoreInProgress(false);
      setBackupFile(null);
      
      // Refresh collections after restore
      fetchCollections();
      fetchDatabaseStatus();
      
      alert('Database restored successfully');
    } catch (err) {
      console.error('Error restoring database:', err);
      setRestoreInProgress(false);
      alert(`Failed to restore database: ${err.message || 'Unknown error'}`);
    }
  };

  const handleClearCollection = async (collectionName) => {
    if (window.confirm(`Are you sure you want to clear all data from the ${collectionName} collection? This action cannot be undone.`)) {
      try {
        await axios.delete(`/api/admin/database/collections/${collectionName}/clear`);
        
        // Update the collection in the list
        setCollections(collections.map(col => 
          col.name === collectionName ? { ...col, count: 0, size: 0 } : col
        ));
        
        // If this is the selected collection, update the displayed data
        if (selectedCollection && selectedCollection.name === collectionName) {
          setCollectionData([]);
          setSelectedCollection({
            ...selectedCollection,
            count: 0,
            size: 0
          });
        }
        
        alert(`Collection ${collectionName} cleared successfully`);
      } catch (err) {
        console.error(`Error clearing collection ${collectionName}:`, err);
        alert(`Failed to clear collection: ${err.message || 'Unknown error'}`);
      }
    }
  };

  const handleExportCollection = async (collectionName, customParams = null) => {
    try {
      setExportLoading(true);
      
      // Prepare query parameters
      let params = {};
      
      if (customParams) {
        params = { ...customParams };
      } else if (selectedCollection?.name === collectionName && filterApplied) {
        // Apply filters if they are active for this collection
        if (selectedClient) params.userId = selectedClient;
        if (selectedOrganization) params.organizationId = selectedOrganization;
      }
      
      // Make the request
      const response = await axios.get(
        `/api/admin/database/collections/${collectionName}/export`, 
        { 
          params,
          responseType: 'blob' 
        }
      );
      
      // Create a blob and download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `${collectionName}-${date}.csv`);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Update download history
      const currentTime = new Date();
      setDownloadHistory(prev => ({
        ...prev,
        [collectionName]: currentTime
      }));
      
      setExportLoading(false);
      
    } catch (err) {
      console.error(`Error exporting collection ${collectionName}:`, err);
      setError(`Failed to export collection ${collectionName}. ${err.response?.data?.msg || err.message}`);
      setExportLoading(false);
    }
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const renderDatabaseStatus = () => {
    return (
      <div className="admin-section">
        <h2 className="admin-section-title">
          <FaDatabase /> MongoDB Atlas Status
        </h2>
        
        <div className="database-collection-card">
          <div className="database-collection-header">
            <div className="database-collection-title">
              Connection Status
              {dbStatus.status === 'connected' ? (
                <span className="admin-badge admin-badge-success">
                  <FaCheck /> Connected
                </span>
              ) : (
                <span className="admin-badge admin-badge-danger">
                  <FaExclamationTriangle /> Disconnected
                </span>
              )}
            </div>
            <button 
              className="admin-btn" 
              onClick={fetchDatabaseStatus}
              title="Refresh Status"
            >
              <FaRedo />
            </button>
          </div>
          
          <div className="database-stats">
            <div className="database-stat">
              <div className="database-stat-label">MongoDB Version</div>
              <div className="database-stat-value">{dbStatus.version || 'Unknown'}</div>
            </div>
            <div className="database-stat">
              <div className="database-stat-label">Total Size</div>
              <div className="database-stat-value">
                {dbStatus.size ? formatBytes(dbStatus.size) : 'Unknown'}
              </div>
            </div>
            <div className="database-stat">
              <div className="database-stat-label">Last Connected</div>
              <div className="database-stat-value">
                {dbStatus.connectionTime ? new Date(dbStatus.connectionTime).toLocaleString() : 'Unknown'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBackupRestore = () => {
    return (
      <div className="admin-section">
        <h2 className="admin-section-title">
          <FaDownload /> Database Backup
        </h2>
        
        <div className="database-collection-card">
          <div className="admin-info-box">
            <FaInfoCircle /> 
            <span>
              Regular backups are essential for data safety. We recommend creating a backup before making significant changes.
            </span>
          </div>
          
          <div className="admin-backup-status" style={{ margin: '15px 0', padding: '10px', backgroundColor: 'rgba(210, 186, 123, 0.1)', borderRadius: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>Last Backup:</strong> {backupStatus.lastBackup ? 
                  new Date(backupStatus.lastBackup).toLocaleString() : 
                  'No recent backups'}
              </div>
              <button 
                className="admin-btn" 
                onClick={handleBackupDatabase}
                disabled={backupStatus.inProgress}
                style={{ backgroundColor: '#243444', color: '#FFFAFA' }}
              >
                <FaDownload /> {backupStatus.inProgress ? 'Creating Backup...' : 'Create Backup'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCollectionList = () => {
    return (
      <div className="admin-section">
        <h2 className="admin-section-title">
          <FaTable /> Database Collections
        </h2>
        
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Collection Name</th>
                <th>Documents</th>
                <th>Size</th>
                <th>Last Downloaded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {collections.length === 0 ? (
                <tr>
                  <td colSpan="5" className="admin-no-data">No collections found</td>
                </tr>
              ) : (
                collections.map(collection => (
                  <tr 
                    key={collection.name} 
                    className={selectedCollection?.name === collection.name ? 'selected-row' : ''}
                    onClick={() => handleCollectionClick(collection)}
                  >
                    <td>{collection.name}</td>
                    <td>{collection.count.toLocaleString()}</td>
                    <td>{formatBytes(collection.size)}</td>
                    <td>
                      {downloadHistory[collection.name] ? 
                        new Date(downloadHistory[collection.name]).toLocaleString() : 
                        'Never'}
                    </td>
                    <td>
                      <div className="admin-actions">
                        <button 
                          className="admin-action-btn" 
                          onClick={(e) => handleViewCollection(collection, e)}
                          title="View Collection"
                          style={{ backgroundColor: 'rgba(36, 52, 68, 0.1)', color: '#243444' }}
                        >
                          <FaTable />
                        </button>
                        <button 
                          className="admin-action-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportCollection(collection.name);
                          }}
                          title="Export Collection"
                        >
                          <FaDownload />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderFilterPanel = () => {
    if (!selectedCollection) return null;
    
    return (
      <div className="admin-section">
        <h2 className="admin-section-title">
          <FaFilter /> Filter Data
        </h2>
        
        <div className="admin-filter-panel">
          <div className="admin-filter-row">
            <div className="admin-filter-group">
              <label>Client:</label>
              <select 
                value={selectedClient} 
                onChange={(e) => setSelectedClient(e.target.value)}
                className="admin-select"
              >
                <option value="">All Clients</option>
                {clients.map(client => (
                  <option key={client._id} value={client._id}>
                    {client.name} ({client.email})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="admin-filter-group">
              <label>Organization:</label>
              <select 
                value={selectedOrganization} 
                onChange={(e) => setSelectedOrganization(e.target.value)}
                className="admin-select"
              >
                <option value="">All Organizations</option>
                {organizations.map(org => (
                  <option key={org} value={org}>
                    {org}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="admin-filter-actions">
            <button 
              className="admin-btn" 
              onClick={handleApplyFilter}
              disabled={dataLoading}
            >
              <FaSearch /> Apply Filter
            </button>
            
            <button 
              className="admin-btn secondary" 
              onClick={handleClearFilters}
              disabled={dataLoading || !filterApplied}
            >
              <FaRedo /> Clear Filters
            </button>
            
            <button 
              className="admin-btn primary" 
              onClick={() => handleExportCollection(selectedCollection.name)}
              disabled={exportLoading}
              style={{backgroundColor: '#243444', color: '#FFFAFA'}}
            >
              <FaFileExport /> {exportLoading ? 'Exporting...' : 'Export as CSV'}
            </button>
          </div>
          
          {filterApplied && (
            <div className="admin-filter-status">
              <p>
                <FaInfoCircle /> Filters applied: 
                {selectedClient && ` Client ID: ${selectedClient}`}
                {selectedOrganization && ` Organization: ${selectedOrganization}`}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCollectionData = () => {
    if (!selectedCollection) return null;
    
    return (
      <div className="admin-section">
        <h2 className="admin-section-title">
          {filterApplied ? 'Filtered Data' : 'Sample Data'}: {selectedCollection.name} 
          {filterApplied && <span className="admin-data-count">({collectionData.length} records)</span>}
        </h2>
        
        {dataLoading ? (
          <div className="admin-loading">Loading collection data...</div>
        ) : collectionData.length === 0 ? (
          <div className="admin-no-data">No data found in this collection</div>
        ) : (
          <div className="admin-data-preview">
            <pre>{JSON.stringify(collectionData, null, 2)}</pre>
          </div>
        )}
      </div>
    );
  };

  if (loading && !selectedCollection) {
    return (
      <div className="admin-container">
        <h1 className="admin-title">Database Management</h1>
        <div className="admin-loading">Loading database information...</div>
      </div>
    );
  }

  if (error && !selectedCollection) {
    return (
      <div className="admin-container">
        <h1 className="admin-title">Database Management</h1>
        <div className="admin-error">{error}</div>
      </div>
    );
  }

  const renderModal = () => {
    if (!modalVisible || !modalCollection) return null;
    
    return (
      <div 
        className="admin-modal-overlay"
        onClick={handleCloseModal}
      >
        <div 
          className="admin-modal"
          style={{
            position: 'absolute',
            left: `${modalPosition.x}px`,
            top: `${modalPosition.y}px`,
            cursor: isDragging ? 'grabbing' : 'default'
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={handleModalMouseDown}
        >
          <div className="admin-modal-header modal-header">
            <h3>
              <FaTable style={{ marginRight: '10px', color: '#D2BA7B' }} />
              {modalCollection.name} Data
              {modalData.length > 0 && (
                <span className="admin-data-count">({modalData.length} records)</span>
              )}
            </h3>
            <button 
              className="admin-modal-close"
              onClick={handleCloseModal}
            >
              &times;
            </button>
          </div>
          
          <div className="admin-modal-body">
            {modalLoading ? (
              <div className="admin-loading">Loading collection data...</div>
            ) : modalData.length === 0 ? (
              <div className="admin-no-data">No data found in this collection</div>
            ) : (
              <div className="admin-modal-data">
                <div className="admin-modal-filter">
                  <h4 className="admin-modal-filter-title">
                    <FaFilter style={{ marginRight: '8px', color: '#D2BA7B' }} /> Filter Data
                  </h4>
                  
                  <div className="admin-modal-filter-row">
                    <div className="admin-modal-filter-group">
                      <label>Organization:</label>
                      <select 
                        value={modalSelectedOrg} 
                        onChange={(e) => setModalSelectedOrg(e.target.value)}
                        className="admin-select"
                      >
                        <option value="">All Organizations</option>
                        {organizations.map(org => (
                          <option key={org} value={org}>
                            {org}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="admin-modal-filter-group">
                      <label>User:</label>
                      <select 
                        value={modalSelectedUser} 
                        onChange={(e) => setModalSelectedUser(e.target.value)}
                        className="admin-select"
                      >
                        <option value="">All Users</option>
                        {clients.map(client => (
                          <option key={client._id} value={client._id}>
                            {client.name} ({client.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="admin-modal-filter-actions">
                    <button 
                      className="admin-btn" 
                      onClick={handleApplyModalFilter}
                      style={{backgroundColor: '#4C5454', color: '#FFFAFA'}}
                    >
                      <FaSearch /> Apply Filter
                    </button>
                    
                    <button 
                      className="admin-btn secondary" 
                      onClick={handleClearModalFilter}
                      disabled={!modalFilterApplied}
                    >
                      <FaRedo /> Clear Filter
                    </button>
                  </div>
                  
                  {modalFilterApplied && (
                    <div className="admin-filter-status">
                      <p>
                        <FaInfoCircle /> Filters applied: 
                        {modalSelectedUser && clients.find(c => c._id === modalSelectedUser) && 
                          ` User: ${clients.find(c => c._id === modalSelectedUser).name}`}
                        {modalSelectedOrg && ` Organization: ${modalSelectedOrg}`}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="admin-modal-actions">
                  <button 
                    className="admin-btn primary" 
                    onClick={handleModalExport}
                    disabled={exportLoading}
                    style={{backgroundColor: '#243444', color: '#FFFAFA'}}
                  >
                    <FaFileExport /> {exportLoading ? 'Exporting...' : 'Export as CSV'}
                  </button>
                </div>
                <div className="admin-data-table-container">
                  <table className="admin-data-table">
                    <thead>
                      <tr>
                        {Object.keys(modalData[0]).map(key => (
                          <th key={key}>{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {modalData.map((item, index) => (
                        <tr key={index}>
                          {Object.values(item).map((value, i) => (
                            <td key={i}>
                              {typeof value === 'object' ? 
                                JSON.stringify(value) : 
                                String(value)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="admin-container">
      <div className="admin-header-with-back">
        <Link to="/admin" className="admin-back-button">
          <FaArrowLeft /> Back to Admin Dashboard
        </Link>
        <h1 className="admin-title">Database Management</h1>
      </div>
      
      {renderDatabaseStatus()}
      {renderCollectionList()}
      {renderBackupRestore()}
      {selectedCollection && renderFilterPanel()}
      {selectedCollection && renderCollectionData()}
      {renderModal()}
    </div>
  );
};

export default DatabaseManagement;
